/**
 * Color Lab tutorials, shown the first time a level with that id opens.
 * The first levels teach one control each; every chapter's first level
 * introduces its new idea. `focus` tells the scene (vials, mist, seals,
 * flask) or the HUD (undo, target, hint, recipes) what to highlight;
 * "demo" keeps a sparkle gliding along the first pour until it is done.
 */
export type LabTutorialFocus =
  | "vials"
  | "spare"
  | "demo"
  | "undo"
  | "target"
  | "hint"
  | "mist"
  | "sizes"
  | "seal"
  | "flask"
  | "recipes";

export interface LabTutorialStep {
  readonly text: string;
  readonly focus?: LabTutorialFocus;
}

export interface LabTutorial {
  readonly id: string;
  readonly steps: readonly LabTutorialStep[];
}

export const COLOR_LAB_TUTORIALS: readonly LabTutorial[] = [
  {
    id: "lab-pour",
    steps: [
      { text: "Chào mừng tới Phòng Pha Màu! Mấy lọ tinh chất bị lẫn lộn hết trơn, mình gom lại cho gọn nha.", focus: "vials" },
      { text: "Chạm lọ đang sáng rồi chạm lọ được chỉ tới để rót. Màu chỉ rót lên lọ trống hoặc lên đúng màu đó thôi.", focus: "demo" },
    ],
  },
  {
    id: "lab-cork",
    steps: [{ text: "Gom đủ các phần cùng màu vào một lọ là lọ được đậy nút bần, xong một màu luôn!", focus: "vials" }],
  },
  {
    id: "lab-undo",
    steps: [
      { text: "Lỡ tay rót nhầm hả? Bấm hoàn tác thoải mái, không tốn xu nào hết.", focus: "undo" },
      { text: "Số mục tiêu nằm trên đầu nè. Lời giải cuối cùng gọn trong mục tiêu là rinh trọn ba sao.", focus: "target" },
    ],
  },
  {
    id: "lab-hint",
    steps: [{ text: "Bí quá thì bấm bóng đèn nha. Mỗi màn có 2 lượt gợi ý miễn phí, hết thì đổi vàng lấy thêm được.", focus: "hint" }],
  },
  {
    id: "lab-tight",
    steps: [{ text: "Kệ chật rồi nè! Chỉ còn đúng một lọ trống, rót bừa là kẹt liền. Tính trước vài bước nha.", focus: "spare" }],
  },
  {
    id: "lab-mist",
    steps: [
      { text: "Sương mờ che mất mấy lớp dưới đáy lọ. Rót lớp trên đi thì màu bên dưới mới chịu ló ra.", focus: "mist" },
      { text: "Chưa biết màu gì thì chừa đường lui, đừng vội lấp kín lọ trống nha.", focus: "spare" },
    ],
  },
  {
    id: "lab-sizes",
    steps: [{ text: "Lọ cao lọ thấp! Màu nào có bao nhiêu phần thì phải vào lọ vừa khít bấy nhiêu mới được đậy nút.", focus: "sizes" }],
  },
  {
    id: "lab-seal",
    steps: [{ text: "Lọ dán con dấu vàng đang bị khóa. Đậy nút xong màu trên con dấu là lọ tự mở liền.", focus: "seal" }],
  },
  {
    id: "lab-shades",
    steps: [{ text: "Màu gần giống nhau tới hoa cả mắt! Nhìn ký hiệu trên từng lớp là phân biệt được liền.", focus: "vials" }],
  },
  {
    id: "lab-flask",
    steps: [
      { text: "Đây là bình pha thần kỳ. Rót vào một phần màu gốc, thêm một phần màu gốc khác là hóa màu mới.", focus: "flask" },
      { text: "Công thức nằm ngay trên đầu nè, quên thì cứ liếc lên, không ai chấm điểm trí nhớ đâu.", focus: "recipes" },
      { text: "Pha xong nhớ rót màu mới ra lọ. Bình pha phải trống trơn khi xong màn nha.", focus: "flask" },
    ],
  },
  {
    id: "lab-mist-seal",
    steps: [{ text: "Sương mờ và con dấu cùng xuất hiện! Màu nào ló ra thì tính đường cho màu đó trước nha.", focus: "seal" }],
  },
  {
    id: "lab-odd-flask",
    steps: [{ text: "Lọ cao thấp gặp bình pha! Pha bao nhiêu thì nhớ đong cho vừa lọ của màu đó nha.", focus: "sizes" }],
  },
  {
    id: "lab-masterpiece",
    steps: [{ text: "Chương kiệt tác! Mọi bí kíp đều có mặt, cứ thong thả mà pha, không ai hối đâu.", focus: "vials" }],
  },
];

export const COLOR_LAB_TUTORIAL_BY_ID: ReadonlyMap<string, LabTutorial> = new Map(COLOR_LAB_TUTORIALS.map((t) => [t.id, t]));
